const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ""

if (!API_BASE_URL && import.meta.env.MODE !== "test") {
  throw new Error("VITE_API_BASE_URL is not defined")
}

const join = (path: string) => `${API_BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`

export const endpoints = {
  // Dashboard
  dashboard: {
    catalog: join("dashboard/catalog"),
    leadingBase: join("dashboard/leading/base"),
    widgetsQuery: join("dashboard/widgets/query"),
  },

  // Authentication
  auth: {
    login: join("users/login"),
  },

  // Form Responses
  formResponses: {
    get: join("form-responses/:id"),
    addSignature: join("form-responses/:id/signatures"),
    addAttachment: join("form-responses/:id/attachments"),
    addFieldMediaAttachment: join("form-responses/:id/field-media/attachments"),
    previewAttachmentUrl: join("form-responses/:id/attachments/:attachmentId/preview-url"),
    previewFieldMediaAttachmentUrl: join(
      "form-responses/:id/field-media/attachments/:attachmentId/preview-url",
    ),
    previewAttachment: join("form-responses/:id/attachments/:attachmentId/preview"),
    previewFieldMediaAttachment: join(
      "form-responses/:id/field-media/attachments/:attachmentId/preview",
    ),
    deleteAttachment: join("form-responses/:id/attachments/:attachmentId"),
    deleteFieldMediaAttachment: join("form-responses/:id/field-media/attachments/:attachmentId"),
    updateFieldMediaNote: join("form-responses/:id/field-media/note"),
    deleteSignature: join("form-responses/:id/signatures/:signatureIndex"),
  },

  // Incidents
  incidents: {
    list: join("incidents"),
    get: join("incidents/:id"),
    getStructured: join("incidents/:id/structured"),
    listStructured: join("incidents/structured"),
    listArchived: join("incidents/archived"),
    workflowOverview: join("incidents/workflow-overview"),
    transition: join("incidents/:id/transition"),
    notify: join("incidents/:id/notify"),
    auditLog: join("incidents/:id/audit-log"),
    summaries: join("incidents/summaries"),
    archive: join("incidents/:id/archive"),
    unarchive: join("incidents/:id/unarchive"),
    update: join("incidents/:id"),
    delete: join("incidents/:id"),
    changeStatus: join("incidents/:id/status"),
    exportPdf: join("pdf/incident"),
    changeAssignedTo: join("incidents/:id/assigned-to"),
    changeRestricted: join("incidents/:id/restricted"),
    changeRecordable: join("incidents/:id/recordable"),
    changeLostTime: join("incidents/:id/lost-time"),
    sendChat: join("incidents/:id/chats"),
    getChats: join("incidents/:id/chats"),
    deleteChats: join("incidents/:id/chats"),
    updateMedical: join("incidents/:id/medical"),
    changeExcludeFromRecordableStats: join("incidents/:id/exclude-from-recordable-stats"),
  },

  // Inspections
  inspections: {
    list: join("inspections"),
    get: join("inspections/:id"),
    listArchived: join("inspections/archived"),
    workflowOverview: join("inspections/workflow-overview"),
    transition: join("inspections/:id/transition"),
    notify: join("inspections/:id/notify"),
    auditLog: join("inspections/:id/audit-log"),
    archive: join("inspections/:id/archive"),
    unarchive: join("inspections/:id/unarchive"),
    update: join("inspections/:id"),
    delete: join("inspections/:id"),
    changeStatus: join("inspections/:id/status"),
    changeAssignedTo: join("inspections/:id/assigned-to"),
    getStructured: join("inspections/:id/structured"),
    exportPdf: join("pdf/inspection"),
  },

  // Issues
  issues: {
    list: join("issues"),
    get: join("issues/:id"),
    listArchived: join("issues/archived"),
    workflowOverview: join("issues/workflow-overview"),
    transition: join("issues/:id/transition"),
    notify: join("issues/:id/notify"),
    auditLog: join("issues/:id/audit-log"),
    archive: join("issues/:id/archive"),
    unarchive: join("issues/:id/unarchive"),
    update: join("issues/:id"),
    delete: join("issues/:id"),
    getStructured: join("issues/:id/structured"),
    exportPdf: join("pdf/issue"),
    changeAssignedTo: join("issues/:id/assigned-to"),
  },

  // Forms
  forms: {
    list: join("forms"),
    get: join("forms/:id"),
    create: join("forms"),
    update: join("forms/:id"),
    delete: join("forms/:id"),
  },

  // Locations
  locations: {
    list: join("locations"),
    get: join("locations/:id"),
    getHours: join("locations/hours"),
    create: join("locations"),
    delete: join("locations/:id"),
    uploadHours: join("locations/hours/upload"),
    uploadHoursJson: join("locations/hours/upload-json"),
  },

  // Users
  users: {
    list: join("users"),
    get: join("users/:id"),
    accessContext: join("users/me/access-context"),
    dashboardPreferences: join("users/me/dashboard-preferences"),
    add: join("users"),
    update: join("users/:id"),
    delete: join("users/:id"),
    updateAdmin: join("users/:userId/admin"),
    invite: join("users/invite"),
    getInvitations: join("users/invitations"),
    getAdminApps: join("users/:userId/admin-apps"),
    assignRole: join("users/:userId/roles"),
    removeRole: join("users/:userId/roles/:roleId"),
    entities: join("users/:userId/entities"),
    entityCounts: join("users/entity-counts"),
  },

  // Groups
  groups: {
    list: join("groups"),
    get: join("groups/:id"),
    create: join("groups"),
    update: join("groups/:id"),
    delete: join("groups/:id"),
    changeGroup: join("users/:userId/group"),
  },

  // List Items (System)
  system: {
    listDefinitions: join("system/list-definitions"),
    listItems: join("system/list-items"),
  },

  // Custom Lists
  custom: {
    listDefinitions: join("custom/list-definitions"),
    listItems: join("custom/list-items"),
    createList: join("custom/lists"),
    updateList: join("custom/lists/:listId"),
    deleteList: join("custom/lists/:listId"),
    getListUsage: join("custom/lists/:listId/usage"),
  },

  // Answer Sets
  answerSets: {
    list: join("answer-sets"),
    create: join("answer-sets"),
    update: join("answer-sets/:id"),
    delete: join("answer-sets/:id"),
  },

  // Organizations
  organizations: {
    list: join("organizations"),
    hierarchy: join("organizations/hierarchy"),
    accessHierarchy: join("organizations/access-hierarchy"),
    entityUsers: join("organizations/entities/:entityId/users"),
  },

  // Actions
  actions: {
    list: join("actions"),
    get: join("actions/:id"),
    create: join("actions"),
    update: join("actions/:id"),
    updateStatus: join("actions/:id/status"),
    addComment: join("actions/:id/comments"),
    delete: join("actions/:id"),
    addAttachment: join("actions/:id/attachments"),
    deleteAttachment: join("actions/:id/attachments/:attachmentId"),
  },

  // Status
  status: {
    alive: join("status/alive"),
  },

  // Support
  support: {
    tickets: join("support/tickets"),
  },

  // Public Shares
  public: {
    formStructureShares: join("public/form-structure-shares"),
    formStructureShare: join("public/form-structure-shares/:shareId"),
  },

  // Roles
  roles: {
    list: join("roles"),
  },

  // Cross-App Token
  crossAppToken: {
    create: join("cross-app-token"),
  },

  // Super Admin
  superAdmin: {
    clerk: {
      organizations: join("super-admin/clerk/organizations"),
      organization: join("super-admin/clerk/organizations/:clerkOrgId"),
      organizationUsers: join("super-admin/clerk/organizations/:clerkOrgId/users"),
      organizationAppAccess: join("super-admin/clerk/organizations/:clerkOrgId/app-access"),
      updateUserPermissions: join("super-admin/clerk/users/:clerkUserId/permissions"),
    },
    vivid: {
      organizations: join("super-admin/vivid/organizations"),
      createOrganization: join("super-admin/vivid/organizations"),
      linkClerkOrganization: join("super-admin/vivid/organizations/:vividOrgId/clerk-link"),
      organizationUsers: join("super-admin/vivid/organizations/:vividOrgId/users"),
      userVisibilityDiagnostics: join(
        "super-admin/vivid/organizations/:vividOrgId/users/:userId/visibility-diagnostics",
      ),
      createOrganizationUser: join("super-admin/vivid/organizations/:vividOrgId/users"),
    },
    refined: {
      organizations: join("super-admin/refined/organizations"),
      createOrganization: join("super-admin/refined/organizations"),
      linkClerkOrganization: join("super-admin/refined/organizations/:refinedOrgId/clerk-link"),
    },
    ask: {
      tenants: join("super-admin/ask/tenants"),
      createTenant: join("super-admin/ask/tenants"),
      linkClerkOrganization: join("super-admin/ask/tenants/:tenantId/clerk-link"),
    },
    dataIntegrity: {
      clerkIds: join("super-admin/data-integrity/clerk-ids"),
      duplicateUsers: join("super-admin/data-integrity/duplicate-users"),
    },
  },
} as const

export type Endpoints = typeof endpoints
